import { useCallback, useRef } from 'react';

import errorLogger, {
  BUGSNAG_STATUSES,
} from '@picsart/growth-rc/services/errorLogger';
import useDebounce from '@picsart/growth-rc/useDebounce';

import useAuth from '@/hooks/useAuth';
import openRegistrationModal from '@/utils/user/openRegistrationModal';

import { togglePresetLike } from '../api/likesApi';
import sendPresetLikeToggled from '../analytics';
import useMyLikedIds from './useMyLikedIds';

import type { ScreenType } from '@/types/analytics';

interface UsePresetLikeParams {
  /** The likes service keys on the CMS documentId; `slug` is for analytics. */
  documentId: string | undefined;
  slug: string;
  screenType: ScreenType;
}

/**
 * How long a preset's writes wait for the user to settle. One tap makes two —
 * the Drive record, then the service — and firing them per tap lets a rapid
 * toggle land them out of order, leaving the stored list and the service
 * disagreeing. Waiting costs one pair of writes per burst however many taps it
 * held.
 *
 * The settled state is always written, even when it matches where the burst
 * began: both calls are idempotent, and skipping would mean tracking what the
 * server holds — a shadow that goes stale the moment the liked set resolves
 * after mount, silently dropping the next change.
 */
const LIKE_SETTLE_MS = 500;

interface WriteLiked {
  (params: { documentId: string; isLiked: boolean }): void;
}

interface PersistLiked {
  (): Promise<{ fileId: string | null } | null>;
}

/**
 * The user's own record first, the service second — and the record rolled back
 * if the service refuses. See hooks.md for why the order matters.
 *
 * The service's own response is discarded: it carries a global tally, and no
 * surface shows one. The call is made so the tally keeps accruing, not so
 * anything can read it back.
 *
 * `isStillWanted` guards the rollback: answers arrive out of order, and undoing
 * a state the user has since tapped past would clobber their newer intent.
 */
const persistToggle = async ({
  writeLiked,
  persistLiked,
  documentId,
  willLike,
  isStillWanted,
}: {
  writeLiked: WriteLiked;
  persistLiked: PersistLiked;
  documentId: string;
  willLike: boolean;
  isStillWanted: () => boolean;
}) => {
  // null = the Drive write failed and already toasted; `useMyLikedIds` resyncs
  // the heart from the file, so the service must not be told either.
  const saved = await persistLiked();

  if (!saved) return;

  try {
    await togglePresetLike({ documentId, isLiked: willLike });
  } catch (error) {
    errorLogger.trigger(error as Error, BUGSNAG_STATUSES.WARN);

    if (!isStillWanted()) return;

    writeLiked({ documentId, isLiked: !willLike });
    await persistLiked();
  }
};

/**
 * One preset's like state and its toggle. Liked comes from the user's Drive set,
 * so a heart makes no request of its own and shows no number — the count exists
 * on the service but is deliberately not surfaced.
 */
const usePresetLike = ({
  documentId,
  slug,
  screenType,
}: UsePresetLikeParams) => {
  const { isAuthorized } = useAuth();
  const { likedIds, writeLiked, toggleLiked, persistLiked } = useMyLikedIds();
  const debounce = useDebounce(LIKE_SETTLE_MS);

  const isLiked = Boolean(documentId) && likedIds.has(documentId as string);

  // The state the user last tapped into. Read through a ref rather than closed
  // over, so a write already scheduled or in flight is measured against the
  // latest answer instead of the tap that started it.
  const wantedRef = useRef(isLiked);

  const toggle = useCallback(() => {
    if (!documentId) return;

    if (!isAuthorized) {
      openRegistrationModal();
      return;
    }

    const willLike = toggleLiked(documentId);

    wantedRef.current = willLike;
    sendPresetLikeToggled({ slug, isLiked: willLike, screenType });

    debounce(() => {
      const sending = wantedRef.current;

      persistToggle({
        writeLiked,
        persistLiked,
        documentId,
        willLike: sending,
        isStillWanted: () => wantedRef.current === sending,
      }).catch((error) =>
        errorLogger.trigger(error as Error, BUGSNAG_STATUSES.WARN)
      );
    });
  }, [
    debounce,
    documentId,
    isAuthorized,
    persistLiked,
    screenType,
    slug,
    toggleLiked,
    writeLiked,
  ]);

  return {
    isLiked,
    isVisible: Boolean(documentId),
    toggle,
  };
};

export default usePresetLike;
